import React, {
  useContext,
  ComponentType,
  useState,
  ReactElement,
  useMemo,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import { useParams } from 'react-router-dom';
import { NotFound } from '../NotFound/NotFound';
import { DispatchContext } from '../../contexts/DispatchContext';
import { subViews } from '../../schema/subViews';
import { views } from '../../schema/views';
import { View } from '../../types/view/View';
import { Page } from '../../components/Page/Page';
import Table from '../../components/Table/NewTable';
import { ViewEntity } from '../../types/entities/ViewEntity';
import { SchemaWithClient } from '../../types/SchemaWithClient';
import { toISODateString } from '../../services/toISODateString';
import { useStateContext } from '../../hooks/useStateContext';
import { SlidingPane } from '../../components/Page/SlidingPane';
import { SlidingPaneAddNew } from '../../components/Page/SlidingPaneAddNew';
// @ts-ignore
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import styles from '../../components/Document/styles.module.css';
import { activityReservation } from '../../schema/activityReservation';
import { serviceReservationAddNew } from '../../schema/serviceReservationAddNew';
import { serviceReservationView } from '../../schema/serviceReservationView';
import api from '../../services/api';
import { LoadingPage } from '../LoadingPage/LoadingPage';

const getRequiredColumns = (columns: any, required: any) => {
  if (!required) {
    required = [];
  }

  const newColumns: any = {};
  Object.keys(columns).map((column: any) => {
    if (required.includes(column)) {
    }
    newColumns[`${column}`] = {
      ...columns[`${column}`],
      ['required']: required.includes(column),
    };
  });
  return newColumns;
};

export const ViewPage: ComponentType = () => {
  const { user, sideBarPosition } = useStateContext();
  const { table } = useParams();

  const viewId = NaN;
  const [loadingViewData, setLoadingViewData] = useState(true);
  const [view, setView] = useState<any>({});

  useEffect(() => {
    setLoadingViewData(true);
    api.get(`/users/${user.id}/views/${table}`).then((res) => {
      setLoadingViewData(false);
      setView(res);
    });
  }, [table]);

  const schema: SchemaWithClient<ViewEntity> | undefined = view
    ? views[view?.table]
    : undefined;

  if (loadingViewData) {
    return <LoadingPage />;
  }

  if (!schema) {
    return <NotFound />;
  }

  const schemaWithRequired: any = { ...schema };
  Object.keys(schema?.columns).map((item: any) => {
    if (view.required.includes(item)) {
      const columnCopy: any = {
        ...schemaWithRequired?.columns[`${item}` as keyof {}],
      };
      columnCopy['required'] = true;
      schemaWithRequired.columns[`${item}` as keyof {}] = columnCopy;
    }
  });

  schema.columns = getRequiredColumns(schema.columns, view.required);

  const subViewsForView: any = [];
  const subViewIds: any = [];

  user.views.map((item) => {
    subViewsForView[item.table] = item;
    subViewIds[item.table] = item.id;
  });

  return (
    <Inner
      view={view}
      subViewsForView={subViewsForView}
      subViewIds={subViewIds}
      schema={schema}
    />
  );
};

type InnerProps<T extends ViewEntity> = {
  view: View<T>;
  subViewsForView: any;
  subViewIds: any;
  schema: SchemaWithClient<T>;
};

const reducer = (state: any, action: any) => {
  const STORAGE_KEY = 'panelSize';
  const localStorageItem: any = localStorage.getItem(STORAGE_KEY);
  const parsedValue = JSON.parse(localStorageItem);

  switch (action.type) {
    case 'SET_PANEL_SIZE':
      const data = JSON.stringify({
        fullWidth: action.payload.fullWidth,
        width: action.payload.width,
      });
      localStorage.setItem(STORAGE_KEY, data);

      return {
        ...state,
        fullWidth: action.payload.fullWidth,
        width: action.payload.width,
      };
    default:
      return parsedValue;
  }
};

const Inner = <T extends ViewEntity>({
  view,
  schema,
  subViewsForView,
  subViewIds,
}: InnerProps<T>): ReactElement => {
  const { viewPeriod, viewAgent, sideBarPosition } = useStateContext();
  const dispatch = useContext(DispatchContext);
  const [width, setWidth] = useState(40);
  const [selectedRowId, setSelectedRowId] = useState<any>();
  const [subSelectedRowId, setSubSelectedRowId] = useState<any>();
  const searchParams = useMemo(() => {
    const searchParams = new URLSearchParams();
    if (viewPeriod[0]) {
      searchParams.append('from', toISODateString(viewPeriod[0]));
    }
    if (viewPeriod[1]) {
      searchParams.append('to', toISODateString(viewPeriod[1]));
    }
    if (viewAgent && viewAgent?.id !== undefined) {
      searchParams.append('booking_agent', viewAgent?.id);
    }
    return searchParams;
  }, [viewPeriod, viewAgent]);
  const [openedSlidingPane, setOpenedSlidingPane] = useState<any>({
    schema: schema,
    row: {},
  });
  const [openedSlidingPaneAddNew, setOpenedSlidingPaneAddNew] = useState<any>({
    schema: schema,
    row: {},
  });
  const [inputValue, setInputValue] = useState('');

  const [fullWidth, setFullWidth] = useState(false);
  const [dragged, setDragged] = useState(false);

  const panelRef: any = useRef(null);

  const handlePanelWidth = (fullWidth: boolean) => {
    setFullWidth(fullWidth);
    if (panelRef.current) {
      panelRef.current.resize(fullWidth ? 100 : width);
    }
  };

  const savePanel = useCallback(() => {
    if (panelRef.current) {
      const size = panelRef.current?.getSize();
      setWidth(size);
    }
  }, [fullWidth]);

  useEffect(() => {
    if (!dragged) {
      savePanel();
    }
  }, [dragged]);

  function handleItemSelect(value: any) {
    dispatch({ type: 'SET_VIEW_AGENT', payload: value });
    setInputValue('');
  }

  const [lastForceFetchedStamp, setLastForceFetchedStamp] = useState<
    number | undefined
  >(undefined);

  const [removeInterval, setRemoveInterval] = React.useState(false);

  function fetchRelatedTables(group?: number) {
    if (view.table !== 'group') {
      return;
    }
    setSelectedRowId(group);
  }

  const subSchemas = useMemo(() => {
    const subSchemaResult = {
      group_activity_reservation: subViews.groupActivityReservation(
        selectedRowId?.id
      ),
    };

    subSchemaResult.group_activity_reservation.columns = getRequiredColumns(
      subSchemaResult.group_activity_reservation.columns,
      subViewsForView?.group_activity_reservation?.required
    );

    return subSchemaResult;
  }, [selectedRowId, openedSlidingPane]);

  const fetchDataHandle = () => {
    setLastForceFetchedStamp(Date.now());
  };

  const removeLocks = () => {
    schema.client.deleteLock(0, {
      schema: JSON.stringify(activityReservation().client),
    });
  };

  const openSlidingPaneHandle = (
    row: any,
    schema: any,
    newEntity?: boolean,
    openInFull?: boolean,
    tab?: string
  ) => {
    if (newEntity) {
      let schemaTemp = schema;
      if (schema?.name === 'Activity Reservation') {
        schemaTemp = activityReservation();
      }

      setOpenedSlidingPaneAddNew({
        schema: schemaTemp,
        row: row === undefined ? {} : row,
        tab: tab,
      });
    } else {
      let schemaTemp = schema;
      if (schema?.name === 'Service Reservations View') {
        schemaTemp = serviceReservationView;
      }
      if (schema?.name === 'Activity Reservation') {
        schemaTemp = activityReservation();
      }

      setOpenedSlidingPane({
        schema: schemaTemp,
        row: row === undefined ? {} : row,
      });
    }
    handlePanelWidth(!!openInFull);
  };

  useEffect(() => {
    document.title = `Zoe - ${schema?.pluralName}`;
  }, [schema]);

  const mainFunctionRef: any = React.useRef(null);
  const childFunctionRef: any = React.useRef(null);

  const handleRefreshMainTableData = () => {
    mainFunctionRef.current();
  };
  const handleRefreshChildTableData = () => {
    childFunctionRef.current();
  };
  /*
  const activityReservationRef = useRef<any>();

  const handleOnSubmit = () => {
    if (activityReservationRef.current) {
      activityReservationRef.current.fetchData();
    }
  };*/

  return (
    <>
      <PanelGroup direction="horizontal">
        <Panel>
          <Page title={view.name}>
            {() => {
              return (
                <PanelGroup key={1} autoSaveId="example" direction="vertical">
                  <Panel
                    className={`${selectedRowId ? '' : 'full-height'} ${
                      styles.Panel
                    }`}
                    collapsible={true}
                    defaultSize={100}
                    order={1}
                  >
                    <Table
                      setFunctionRef={(ref: any) =>
                        (mainFunctionRef.current = ref)
                      }
                      searchParams={searchParams}
                      columnMask={view.columns.map((column) => column.name)}
                      schema={schema}
                      onRowSelected={(row: any) => {
                        fetchRelatedTables(row);
                      }}
                      isView={true}
                      viewId={view.id}
                      pins={view?.pins}
                      selectedRowId={selectedRowId}
                      setCurrentEditedRow={(row: any, openInFull?: boolean) => {
                        console.log('openSlidingPaneHandle 1 ---');
                        openSlidingPaneHandle(row, schema, false, openInFull);
                      }}
                      setCurrentAddNewRow={(row: any) => {
                        const schemaTemp: any =
                          schema.name === 'Service Reservations View'
                            ? serviceReservationAddNew
                            : { ...schema };

                        console.log('openSlidingPaneHandle 2 ---');

                        openSlidingPaneHandle(row, schemaTemp, true);
                      }}
                      hasSlidingPane={true}
                      lastForceFetchedStamp={lastForceFetchedStamp}
                    />
                  </Panel>
                  {selectedRowId && schema.pluralName === 'Groups' && (
                    <>
                      <PanelResizeHandle
                        className={`${styles.ResizeHandleOuter}`}
                        id={'asd'}
                      >
                        <div className={styles.ResizeHandleInner}>
                          <svg className={styles.Icon} viewBox="0 0 24 24">
                            <path
                              fill="currentColor"
                              d="M8,18H11V15H2V13H22V15H13V18H16L12,22L8,18M12,2L8,6H11V9H2V11H22V9H13V6H16L12,2Z"
                            />
                          </svg>
                        </div>
                      </PanelResizeHandle>
                      <Panel
                        className={`${styles.Panel} justify-end`}
                        defaultSize={100}
                        collapsible={true}
                        order={2}
                      >
                        <div className={` max-h-full`}>

                        </div>
                      </Panel>
                    </>
                  )}
                </PanelGroup>
              );
            }}
          </Page>
        </Panel>
        {openedSlidingPane?.row?.id && (
          <>
            <PanelResizeHandle
              className={`${styles.ResizeHandleOuter} bg-[#ddd]`}
            >
              <div className={styles.ResizeHandleInner}>
                <svg className={styles.Icon} viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M8,18H11V15H2V13H22V15H13V18H16L12,22L8,18M12,2L8,6H11V9H2V11H22V9H13V6H16L12,2Z"
                  />
                </svg>
              </div>
            </PanelResizeHandle>
            <Panel
              className={`${styles.Panel}`}
              collapsible={false}
              minSize={20}
              defaultSize={fullWidth ? 100 : 40}
              ref={panelRef}
            >
              <SlidingPane
                setFullWidthProp={(value) => {
                  console.log('setFullWidthProp');
                  handlePanelWidth(value);
                }}
                fullWidth={fullWidth}
                schema={openedSlidingPane?.schema}
                id={
                  openedSlidingPane?.hasOwnPane
                    ? undefined
                    : openedSlidingPane?.row?.id
                }
                onSubmit={() => {
                  if (
                    openedSlidingPane?.schema?.viewTable ===
                    'group_activity_reservations'
                  ) {
                    handleRefreshChildTableData();
                  } else {
                    handleRefreshMainTableData();
                  }
                  setOpenedSlidingPane({
                    schema: schema,
                    row: {},
                  });
                }}
                propertiesFromTable={
                  openedSlidingPane?.row ? openedSlidingPane?.row : {}
                }
                onClose={(value) => {
                  setRemoveInterval(true);
                  if (
                    openedSlidingPane?.schema?.viewTable ===
                    'group_activity_reservations'
                  ) {
                    schema.client
                      .deleteLock(value?.id, {
                        schema: JSON.stringify(
                          openedSlidingPane?.schema.client
                        ),
                      })
                      .then((result: any) => {});
                    handleRefreshChildTableData();
                  } else {
                    handleRefreshMainTableData();
                  }
                  setOpenedSlidingPane({
                    schema: schema,
                    row: {},
                  });
                }}
                addNewRow={(schema: any, tab: any) => {
                  console.log('openSlidingPaneHandle 5 ---');
                  openSlidingPaneHandle(
                    {
                      id: 'new',
                      group: openedSlidingPane?.row,
                    },
                    schema,
                    true,
                    false,
                    tab
                  );
                }}
                refreshMainTable={() => {
                  fetchDataHandle();
                }}
              />
            </Panel>
          </>
        )}
      </PanelGroup>
      {openedSlidingPaneAddNew?.row?.id && (
        <>
          <SlidingPaneAddNew
            schema={openedSlidingPaneAddNew?.schema}
            id={
              openedSlidingPaneAddNew?.hasOwnPane
                ? undefined
                : openedSlidingPaneAddNew?.row?.id
            }
            onSubmit={() => {
              setOpenedSlidingPaneAddNew({
                schema: schema,
                row: {},
              });
              fetchDataHandle();
            }}
            propertiesFromTable={
              openedSlidingPaneAddNew?.row ? openedSlidingPaneAddNew?.row : {}
            }
            onClose={(value) => {
              setOpenedSlidingPaneAddNew({
                schema: schema,
                row: {},
              });
            }}
          />
        </>
      )}
    </>
  );
};
